import type { MentoringDataType } from "@/types/mentoring"
import { Chip } from "../molecules"
import { Avatar, Button, Icon, Rating, Text } from "../atoms"
import { formatToBr } from "@/utils/formatDateTime"
import meetIcon from "@/assets/meet-icon.png"
import docsIcon from "@/assets/documentos-google.png"
import { cn } from "@/utils/cn"

export const MentoringsList = ({
  className,
  mentoring,
  onCardClick,
  onRescheduleClick,
  onCancelScheduleClick,
}: {
  mentoring: MentoringDataType
  className?: string
  onCardClick?: () => void
  onRescheduleClick?: () => void
  onCancelScheduleClick?: () => void
}) => {
  // Mapeamento de status baseado no statusMentoria do JSON
  const mentoringStatus: Record<number, string> = {
    1: "Pendente",
    2: "Realizado",
    3: "Finalizado",
    4: "No-show",
    5: "Cancelado",
    6: "Avaliado",
  }

  const mentoringStatusColor: Record<number, "warning" | "info" | "success" | "default" | "error"> =
    {
      1: "warning",
      2: "info",
      3: "success",
      4: "default",
      5: "error",
      6: "success",
    }

  // Helpers para facilitar a leitura e lidar com a mudança de nomes no JSON
  const status = mentoring.statusMentoria ?? mentoring.status_mentoria
  const dataHora = mentoring.dataHoraMentoria ?? mentoring.data_hora_mentoria
  const tema = mentoring.temaMentoria ?? mentoring.tema_mentoria
  const descricao = mentoring.descricaoMentoria ?? mentoring.descricao_mentoria
  const tipo = mentoring.tipoMentoria ?? mentoring.tipo_mentoria
  const link = mentoring.linkMentoria ?? mentoring.link_mentoria
  const reagendarUrl = mentoring.reagendamentoUrl ?? mentoring.reagendamento_url

  // Reagendamento e cancelamento são proibidos nas 24h que antecedem a mentoria
  const LOCK_WINDOW_HOURS = 24
  const startTime = dataHora ? new Date(dataHora).getTime() : NaN
  const canChangeSchedule =
    !Number.isNaN(startTime) && startTime - Date.now() >= LOCK_WINDOW_HOURS * 60 * 60 * 1000
  const lockedMessage = `Reagendamento e cancelamento indisponíveis nas ${LOCK_WINDOW_HOURS}h que antecedem a mentoria`

  // Lógica para definir o ícone com base na URL
  const isMeet = link?.toLowerCase().includes("meet")
  const linkIcon = isMeet ? meetIcon : docsIcon

  return (
    <div
      className={cn(
        "group relative flex flex-col gap-4 overflow-hidden rounded-[8px] border border-transparent bg-dark-500 p-[20px] transition-all hover:z-10 hover:scale-105 hover:border-primary",
        className
      )}
    >
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <Chip variant={status ? mentoringStatusColor[status] : "success"}>
            {status ? mentoringStatus[status] : "Finalizado"}
          </Chip>
          {tipo === 1 && <Chip>In Company</Chip>}
        </div>
        <div className="flex items-center gap-2 text-dark-300">
          <Icon name="calendar" />
          <Text
            variant="small"
            className="text-dark-300"
          >
            {dataHora ? formatToBr(dataHora, { formatType: "datetime" }) : "Data não definida"}
          </Text>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <span
          onClick={onCardClick}
          className="cursor-pointer hover:text-primary hover:underline"
        >
          <Text
            variant="button"
            className="truncate"
          >
            {tema || descricao || "Sem título"}
          </Text>
        </span>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Avatar
              src={mentoring.emp_logo_url}
              alt={mentoring.empNome || mentoring.emp_nome}
              size="sm"
            />
            <Text>{mentoring.empNome || mentoring.emp_nome}</Text>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          {status === 1 && reagendarUrl && (
            <Button
              onClick={onRescheduleClick}
              disabled={!canChangeSchedule}
              title={canChangeSchedule ? undefined : lockedMessage}
              size="sm"
              variant="ghost"
            >
              Reagendar
            </Button>
          )}
          {status === 1 && mentoring.cancelamentoUrl && (
            <Button
              onClick={onCancelScheduleClick}
              disabled={!canChangeSchedule}
              title={canChangeSchedule ? undefined : lockedMessage}
              size="sm"
              variant="ghost"
              className="text-error hover:bg-error/10"
            >
              Cancelar
            </Button>
          )}

          {/* Renderização condicional unificada do Link (Meet ou Docs) */}
          {link && (
            <a
              className="flex size-10 items-center justify-center rounded-full transition-colors hover:bg-primary/20"
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              title={isMeet ? "Abrir Google Meet" : "Abrir Documento"}
            >
              <img
                src={linkIcon}
                alt="Link Icon"
                width="20"
              />
            </a>
          )}
        </div>

        <Rating value={mentoring.mentoradoNota ?? mentoring.nota_mentorado} />
      </div>

      {tipo === 1 && (
        <div className="absolute -bottom-8 right-0 text-[200px] text-dark-400 opacity-20">
          <Icon name="land-layer-location" />
        </div>
      )}
    </div>
  )
}
